import { Modal as ModalModule, UI, Utils } from 'common';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import uniqueId from 'lodash/uniqueId';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import ReactToPrint from 'react-to-print';

const { Session } = Utils;
const { BarcodeGenerator, WrappedButton, WrappedButtonType } = UI;
const { Modal } = ModalModule;

const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

class MailInRefundForm extends React.Component {
	constructor(props) {
		super(props);
		this.rePrintClainFormRef = null;
		this.reactToPrintButton = React.createRef();
	}

	renderPostageList = () => {
		const { data } = this.props;
		const { formData, printDetails } = data;
		const { serviceTypeName } = printDetails;
		const { refunds, toAddress, netstampDetails } = formData;
		const { printTypeId, netstampSummary } = printDetails;
		const items = refunds;
		const {
			name,
			companyName,
			addressLine1,
			addressLine2,
			addressLine3,
			city,
			stateProvince,
			postalCode,
			postalCodeAddon,
		} = toAddress;

		// const items = [];
		let totalFaceValue = 0;

		return items.reduce(
			(result, item, index) => {
				const {
					refundRequestedDate,
					numberOfPieces = 1,
					refundRequestedAmount,
				} = item;
				const subTotal = numberOfPieces * refundRequestedAmount;

				totalFaceValue += subTotal;

				// Part 2a - Unused PC Postage Indicia Submitted
				result.postageIndiciaRows.push(
					<tr key={uniqueId()}>
						<td>{moment(refundRequestedDate).format('MM/DD/YYYY')}</td>
						<td>{numberOfPieces}</td>
						<td>{usCurrencyFormatter.format(refundRequestedAmount)}</td>
						<td>{usCurrencyFormatter.format(subTotal)}</td>
						<td colSpan="2" />
					</tr>
				);

				// Add an end row in part 2a table to show "Total Postage Face Value"
				result.postageIndiciaRows =
					index === items.length - 1
						? [
								...result.postageIndiciaRows,
								<tr key={uniqueId()}>
									<td colSpan="3">
										<b>
											<i>Total Postage Face Value: </i>
										</b>
										{usCurrencyFormatter.format(totalFaceValue)}
									</td>
									<td />
									<td colSpan="2" />
								</tr>,
							]
						: result.postageIndiciaRows;

				// Postage Details - list of the total misprinted or unused postage
				if (printTypeId === 5) {
					if (index === items.length - 1) {
						netstampDetails.forEach(netstampDetail => {
							const { netstampSeries, row, column } = netstampDetail;

							result.postageDetailedRows.push(
								<tr key={uniqueId()}>
									<td>{moment(refundRequestedDate).format('MM/DD/YYYY')}</td>
									<td className="align-left">
										{`${netstampSummary.toString()}`}
										{!isNull(netstampSeries) ? `: ${netstampSeries}, ` : ''}
										{!isNull(row) ? `: Row: ${row}, ` : ''}
										{!isNull(column) ? `Column: ${column}` : ''}
									</td>
									<td>{usCurrencyFormatter.format(subTotal)}</td>
									<td>{serviceTypeName}</td>
								</tr>
							);
						});
					}
				} else {
					result.postageDetailedRows.push(
						<tr key={uniqueId()}>
							<td>{moment(refundRequestedDate).format('MM/DD/YYYY')}</td>
							<td className="align-left">
								{isNull(name) ? '' : `${name},`}
								{isNull(companyName) ? '' : `${companyName},`}
								{isNull(addressLine1) ? '' : `${addressLine1},`} {addressLine2}
								{addressLine3}
								{isNull(city)
									? ''
									: `${city},} {${stateProvince} ${postalCode}`}
								{!isNull(postalCodeAddon) && !isEmpty(postalCodeAddon)
									? `-${postalCodeAddon}`
									: ''}
							</td>
							<td>{usCurrencyFormatter.format(subTotal)}</td>
							<td>{serviceTypeName}</td>
						</tr>
					);
				}

				return result;
			},
			{
				postageIndiciaRows: [],
				postageDetailedRows: [],
			}
		);
	};

	printClaimForm = () => {
		if (this.reactToPrintButton.current) {
			this.reactToPrintButton.current.click();
		}
	};

	render() {
		const { data, modalId } = this.props;
		const { customerDetails = {} } = Session.getAccountInfo();
		const { formData } = data;
		const { fromAddress, refunds, deviceId } = formData;
		const {
			addressLine1 = '',
			addressLine2 = '',
			addressLine3 = '',
			city = '',
			email,
			phone = '',
			stateProvince = '',
			postalCode = '',
			name,
			companyName,
			postalCodeAddon = '',
		} = customerDetails.address || fromAddress;
		const claimNumber = refunds[0].refundMailinBatchid;
		const requestedDate = new Date().toLocaleDateString();
		const { postageIndiciaRows = {}, postageDetailedRows = {} } =
			this.renderPostageList();

		return (
			<Modal
				modalId={modalId}
				className="history-refund mail-in-refund-details-form-modal"
				header="Reprint Claim Form"
				hideFooter
			>
				<div
					className="history-refund mailin-refund-form"
					ref={el => (this.refPrintClaimForm = el)}
				>
					<ReactToPrint
						trigger={() => (
							<button
								type="button"
								style={{ display: 'none' }}
								ref={this.reactToPrintButton}
							>
								Print
							</button>
						)}
						content={() => this.refPrintClaimForm}
					/>
					<WrappedButton
						label="Print this page"
						className="p-col-6 underline print-button"
						dataqa="print-this-page-btn"
						buttonType={WrappedButtonType.TertiaryGrey}
						icon="icon print-page-btn"
						onClick={this.printClaimForm}
					/>
					<div className="page">
						{/* Barcode | Claim Number */}
						<div className="p-grid p-nogutter barcode">
							<div className="barcode-container">
								<p>Claim #: {claimNumber}</p>
								<BarcodeGenerator barcode={claimNumber} />
							</div>
						</div>

						{/* title */}
						<div className="p-grid p-nogutter title">
							<div className="p-col-12">
								<h4>
									USPS<sup>&reg;</sup> PC POSTAGE PRODUCTS
								</h4>
								<h2>
									<strong>Refund Request</strong> for Unused Postage
								</h2>
							</div>
						</div>

						{/* Instructions Section */}
						<div className="p-grid p-nogutter instructions">
							<h4>
								<b>Instructions:</b>
							</h4>
							<p className="p-col-12">
								<i className="pi pi-check" />
								<b>STEP 1:</b> Collect the original misprinted postage and
								postage labels you intended to print on.
							</p>
							<ul className="p-col-12">
								<li>
									You must include the labels intended for postage, even if they
									are usable.
								</li>
								<li>
									If postage did not print, provide a print out of your Print
									History and proceed to step 2. Refunds without postage are
									approved on a case-by-case basis.
								</li>
							</ul>
							<p className="p-col-12">
								<i className="pi pi-check" />
								<b>STEP 2:</b> Sign Part 1 of this Refund Request form in the
								highlighted area below.
							</p>
							<p className="p-col-12">
								<i className="pi pi-check" />
								<b>STEP 3:</b> Enclose the misprinted postage, postage labels,
								this <b>signed</b> Refund Request Form, and Postage Details (see
								last page(s)).
							</p>
							<p className="p-col-12">
								<i className="pi pi-check" />
								<b>STEP 4:</b> Mail the envelope within 60 days of the postage
								print date to:
							</p>
							<p className="p-col-12 mailin-address">
								Attn: Misprint Refunds
								<br />
								4301 Bull Creek Rd Ste 300
								<br />
								Austin, TX 78731-5937
							</p>
						</div>
						<p className="privacy">
							<b>Privacy Act Statement:</b> The collection of this information
							is authorized by 39 USC 401, 404, and 1206; Pub. L. 104-134. This
							information will be used to reimburse you when no service is
							rendered. As a routine use, the information may be disclosed to an
							appropriate government agency, domestic or foreign, for law
							enforcement purposes; where pertinent, in a legal proceeding to
							which the USPS is a party or has an interest; to a government
							agency in order to obtain information relevant to a USPS decision
							concerning employment, security clearances, contracts, licenses,
							grants, permits, or other benefits; to a congressional office at
							your request; to an expert consultant, or other person under
							contract with the USPS to fulfill an agency function; to a Federal
							Records Center for storage; to the Office of Management and Budget
							for review of private relief legislation; to an independent
							certified accountant during an official audit of the USPS
							finances; to an investigator, administrative judge or complaints
							examiner appointed by the Equal Employment Opportunity Commission
							for investigation of a formal EEO complaint under 29 CFR 1614; to
							the Merit Systems Protection Board or Office of Special Counsel
							for proceedings or investigations involving personnel practices
							and other matters within their jurisdiction; to a labor
							organization as required by the National Labor Relations Act; to
							the Department of the treasury for cross-matching under the
							treasury Offset Program; and to a requestor, the name and address
							of the owner of leased property, or of the payee when different
							from the owner. Completion of this form is voluntary; however, if
							this information is not provided, a refund will not be considered.
						</p>
						{/* Part 1 - Application Information */}
						<table className="application-information">
							<tbody>
								<tr>
									<td colSpan="3">
										<h5>
											<b>Part 1 - Application Information</b>{' '}
											<i>(To be completed by customer)</i>
										</h5>
									</td>
								</tr>
								<tr>
									<td colSpan="3">
										<i>Customer/Company Name:</i>
										<br />
										{name} {companyName}
									</td>
								</tr>
								<tr>
									<td colSpan="2">
										<i>Mailing Address:</i>
										<br />
										{addressLine1}
										{addressLine2}
										{addressLine3}
										<br />
										{`${city}, ${stateProvince} ${postalCode}`}
										{postalCodeAddon !== null && !isNull(postalCodeAddon)
											? `-${postalCodeAddon}`
											: ''}
									</td>
									<td colSpan="1">
										<i>PC Postage Device ID:</i>
										<br />
										{deviceId}
									</td>
								</tr>
								<tr>
									<td colSpan="2">
										<i>Email:</i>
										<br />
										{email === null ? customerDetails.email : email}
									</td>
									<td colSpan="1">
										<i>Contact Phone:</i>
										<br />
										{phone === null ? customerDetails.address.phone : phone}
									</td>
								</tr>
								<tr>
									<td colSpan="3">
										<b>
											<i>Certification Statement:</i>
										</b>{' '}
										<i>
											I certify that I have not used the postage I am submitting
											for a misprint refund and that the United States Postal
											Service has not performed any service related to this
											postage. I understand that this claim will be verified by
											the United States Postal Service and/or its authorized
											representative. I also understand that filing false,
											fictitious or fraudulent refund requests for the return of
											U.S. postage is punishable under criminal laws of the
											United States, including 18 U.S.C. &sect; 1001.
										</i>
									</td>
								</tr>
								<tr>
									<td colSpan="2" className="signature-cell">
										<div className="signature">
											<h4>
												<b className="sign">Sign Here</b>
											</h4>
										</div>
									</td>
									<td colSpan="1">
										<i>Date Of Request:</i>
										<br />
										{requestedDate}
									</td>
								</tr>
							</tbody>
						</table>
						<h3>
							<b>For Official Use Only:</b>
						</h3>
						{/* Part 2a - Unused PC Postage Indicia Submitted */}
						<table className="unused-postage-indicia-table">
							<tbody>
								<tr>
									<td colSpan="4">
										<h5>
											<b>Part 2a - Unused PC Postage Indicia Submitted</b>
										</h5>
									</td>
									<td colSpan="2">
										<h5>
											<b>Part 2b - Verification of Indicia</b>
										</h5>
									</td>
								</tr>
								<tr>
									<td>
										<b>
											<i>Date in Indicia:</i>
										</b>
									</td>
									<td>
										<b>
											<i># of Pieces:</i>
										</b>
									</td>
									<td>
										<b>
											<i>Face Value Each Piece:</i>
										</b>
									</td>
									<td>
										<b>
											<i>Total Face Value:</i>
										</b>
									</td>
									<td colSpan="2">
										<b>
											<i>
												Verified Amount (if different than Total Face Value):
											</i>
										</b>
									</td>
								</tr>
								{postageIndiciaRows}
							</tbody>
						</table>

						{/* Part 3 - Calculation and Verification Of Refund */}
						<table className="calculation-verification-refund-table">
							<tbody>
								<tr>
									<td colSpan="6">
										<h5>
											<b>Part 3 - Calculation and Verification Of Refund</b>
										</h5>
									</td>
								</tr>
								<tr>
									<td colSpan="3">
										<i>Total Verified Amount:</i>
									</td>
									<td colSpan="3">
										<i>Reimbursement to be Reported to USPS:</i>
									</td>
								</tr>
								<tr>
									<td colSpan="3">
										<i>Less Processing Fee: </i> N/A
									</td>
									<td colSpan="3">
										<i>Number Of Indicia:</i>
									</td>
								</tr>
								<tr>
									<td colSpan="3">
										<i>Total Refund Claimant is Entitled:</i>
									</td>
									<td colSpan="3">
										<i>Reimbursable Amount:</i>
									</td>
								</tr>
								<tr>
									<td colSpan="2">
										<i>PC Postage Provider:</i>
										<br />
										<p>
											ATTN: MISPRINT REFUNDS
											<br />
											4301 Bull Creek Rd Ste 300
											<br />
											Austin, TX 78731-5937
										</p>
									</td>
									<td colSpan="2">
										<i>Certifying Employee Signature:</i>
									</td>
									<td colSpan="2">
										<i>Date Refund Issued:</i>
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<div className="page">
						{/* Postage Details Section */}
						<h4>
							USPS<sup>&reg;</sup> PC POSTAGE PRODUCTS
						</h4>
						<h2>Postage Details</h2>
						<p>
							This is a detailed list of the total misprinted or unused postage
							you selected for refund. Please be sure to include this list with
							your refund submission. The USPS will use this information to
							ensure the postage and/or labels were not used.
						</p>
						<table className="postage-details-table">
							<thead>
								<tr>
									<th>Print Date</th>
									<th className="align-left">
										NetStamps Label Number or Recipient
									</th>
									<th>Total Postage Amount</th>
									<th>Mail Class</th>
								</tr>
							</thead>
							<tbody>{postageDetailedRows}</tbody>
						</table>
					</div>
				</div>
			</Modal>
		);
	}
}

MailInRefundForm.propType = {
	formData: PropTypes.object.isRequired,
};

export default MailInRefundForm;
